import _ from 'underscore'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import FollowMouse from 'pages/shared/FollowMouse'

import LegendConfig from 'pages/Device/ThermostatRthPanel/Legend/legendItemsConfig'
import SeriesLabel from 'pages/Device/ThermostatRthPanel/Legend/SeriesLabel'
import HoveredSeriesValue from 'pages/Device/ThermostatRthPanel/Legend/HoveredSeriesValue'
import { GetLegendTooltipData } from 'pages/Device/ThermostatRthPanel/Legend/tooltipDataBuilder'
import HoverTooltip from './HoverTooltip'
import { RuntimeHistory } from 'types/RuntimeHistory'
import { zoneColors } from './chartOptions'

import { color, hexToRgb } from 'styles/colors'
interface ZoneSeries {
  name: string;
  series: Array<string> | null
}
interface SelectedLegends {
  series: Array<string> | null
  zoneSeries: Array<ZoneSeries> | null
  alerts: Array<string> | null
}

type Props = {
  timeAtCursor: number
  rthData: RuntimeHistory | null
  selectedLegends: SelectedLegends
  showHoverData?: boolean
}

const HoverBox = ({ selectedLegends, timeAtCursor, rthData, showHoverData = true }: Props) => {
  const hoverData = GetLegendTooltipData(timeAtCursor / 1000, rthData)
  const flattenedLegendConfig = _.flatten(_.values(LegendConfig))
  const filteredLegendConfig = _.filter(flattenedLegendConfig, (item) => _.includes(selectedLegends.series || [], item.seriesName))
  const [showIndoor, setShowIndoor] = useState(true)
  const [showIndoorUnit, setShowIndoorUnit] = useState(true)
  const [showOutdoor, setShowOutdoor] = useState(true)
  const [showOutdoorUnit, setShowOutdoorUnit] = useState(true)

  const isControlHub = rthData ? rthData.isControlHub : false

  const getIndoorLegendSeries = (name) => {
    return _.find(flattenedLegendConfig, (config) => config.seriesName === name)
  }

  const getIndoorUnitLegendSeries = useCallback((name) => {
    const indoorUnitLegendItems = isControlHub
      ? _.union(
        LegendConfig.INDOOR_UNIT_LEGEND_ITEMS,
        LegendConfig.CONTROL_HUB_INDOOR_UNIT_LEGEND_ITEMS
      ) : LegendConfig.INDOOR_UNIT_LEGEND_ITEMS

    return _.find(indoorUnitLegendItems, (config) => config.seriesName === name)
  }, [isControlHub])

  const getOutdoorLegendSeries = (name) => {
    return _.find(LegendConfig.OUTDOOR_LEGEND_ITEMS, (config) => config.seriesName === name)
  }

  const getOutdoorUnitLegendSeries = (name) => {
    return _.find(LegendConfig.OUTDOOR_UNIT_LEGEND_ITEMS, (config) => config.seriesName === name)
  }

  useEffect(() => {
    const indoorSeries = selectedLegends.zoneSeries !== null && selectedLegends.zoneSeries?.map((item) => !_.isEmpty(item.series)).includes(true)
    const indoorUnitSeries = filteredLegendConfig.map((series) => getIndoorUnitLegendSeries(series.seriesName) !== undefined).includes(true)
    const outdoorSeries = filteredLegendConfig.map((series) => getOutdoorLegendSeries(series.seriesName) !== undefined).includes(true)
    const outdoorUnitSeries = filteredLegendConfig.map((series) => getOutdoorUnitLegendSeries(series.seriesName) !== undefined).includes(true)

    setShowIndoor(indoorSeries)
    setShowIndoorUnit(!!indoorUnitSeries)
    setShowOutdoor(!!outdoorSeries)
    setShowOutdoorUnit(!!outdoorUnitSeries)
  }, [selectedLegends.zoneSeries, filteredLegendConfig, getIndoorUnitLegendSeries])

  const getZoneLegendData = (zoneName, seriesName) => {
    const zone = _.find(hoverData.zones, (zone) => zone.name === zoneName)
    if (zone && zone[seriesName]) {
      return zone[seriesName]
    } else {
      return 'No Data'
    }
  }

  return (
    <FollowMouse display={!!timeAtCursor}>
      <S.HoverBox>
        <div>
          <S.Section>
            {showIndoor &&
              <div>
                <S.HeaderText>Indoor</S.HeaderText>
                <hr />
              </div>
            }
            <S.SectionData>
              <div>
                {_.map(selectedLegends.zoneSeries || [], (item, index) => (
                  !_.isEmpty(item.series) && (
                    _.map(item.series || [], (series) => {
                      const indoorLegendSeries = getIndoorLegendSeries(series)
                      return (
                        indoorLegendSeries && (
                        <div key={series + item.name}>
                          <S.SeriesLabel isSelected series={{ ...indoorLegendSeries, stroke: zoneColors[index] }} disable={false} />
                          <S.HoveredSeriesValue isSelected series={indoorLegendSeries} getLegendDataCallback={() => getZoneLegendData(item.name, series)} styled={false} />
                        </div>)
                      )
                    })
                  )
                ))}
                {filteredLegendConfig.map((series) => {
                  const LegendSeries = getIndoorUnitLegendSeries(series.seriesName)

                  return (
                    LegendSeries && (
                    <div key={series.seriesName}>
                      <S.SeriesLabel isSelected series={LegendSeries} disable={false} />
                      <S.HoveredSeriesValue isSelected series={LegendSeries} getLegendDataCallback={() => hoverData[series.seriesName] || 'No Data'} styled={false} />
                    </div>)
                  )
                })}
                { showIndoor && hoverData.zones &&
                  <S.AnscillaryContainer>
                    <S.AnscillaryDataLabel>System Status</S.AnscillaryDataLabel>
                    <S.AnscillaryValueContainer>
                      <S.AnscillaryData key={hoverData.zones[0].name}>{hoverData.zones[0].status || 'No Data'}</S.AnscillaryData>
                    </S.AnscillaryValueContainer>
                  </S.AnscillaryContainer>
                  }
              </div>
              <div>
                {showIndoorUnit && showHoverData && hoverData &&
                  <HoverTooltip timeAtCursor={timeAtCursor} showIndoorUnit rthData={rthData} isControlHub={isControlHub} />
                }
              </div>
            </S.SectionData>
          </S.Section>
          <S.Section>
            {(showOutdoor || showOutdoorUnit) &&
              <div>
                <S.HeaderText>Outdoor</S.HeaderText>
                <hr />
              </div>
            }
            <S.SectionData>
              <div>
                {filteredLegendConfig.map((series) => {
                  const LegendSeries = getOutdoorLegendSeries(series.seriesName)

                  return (
                    LegendSeries && (
                    <div key={series.seriesName}>
                      <S.SeriesLabel isSelected series={LegendSeries} disable={false} />
                      <S.HoveredSeriesValue isSelected series={LegendSeries} getLegendDataCallback={() => hoverData[series.seriesName] || 'No Data'} styled={false} />
                    </div>)
                  )
                })}
                {filteredLegendConfig.map((series) => {
                  const LegendSeries = getOutdoorUnitLegendSeries(series.seriesName)

                  return (
                    LegendSeries && (
                      <div key={series.seriesName}>
                        <S.SeriesLabel isSelected series={LegendSeries} disable={false} />
                        <S.HoveredSeriesValue isSelected series={LegendSeries} getLegendDataCallback={() => hoverData[series.seriesName] || 'No Data'} styled={false} />
                      </div>
                    )
                  )
                })}
              </div>
              <div>
                {showOutdoor && showHoverData && hoverData &&
                  <HoverTooltip timeAtCursor={timeAtCursor} showOutdoor={showOutdoor && !showOutdoorUnit} rthData={rthData} isControlHub={isControlHub} />
                }
                {showOutdoorUnit && showHoverData && hoverData &&
                  <HoverTooltip timeAtCursor={timeAtCursor} showOutdoorUnit rthData={rthData} isControlHub={isControlHub} />
                }
              </div>
            </S.SectionData>
          </S.Section>
        </div>
      </S.HoverBox>
    </FollowMouse>
  )
}

export default HoverBox

const S = {
  Section: styled.div`
    margin: 5px;
  `,
  SectionData: styled.div`
    display: flex;
    gap: 20px;
  `,
  HeaderText: styled.div`
    font-weight: 800;
  `,
  HoverBox: styled.div`
    font-size: 12px;
    background-color: ${hexToRgb(color('maintenanceTrendHoverBoxBackground'), 0.7)};
    border: 1px solid ${color('maintenanceTrendHoverBoxBoarder')};
    padding: 5px 10px;
    pointer-events: none;
    margin: 0 30px;
  `,
  SeriesLabel: styled(SeriesLabel)`
    margin-right: 20px;
    margin-bottom: 5px;
    min-width: 150px;
    font-weight: 500;
  `,
  HoveredSeriesValue: styled(HoveredSeriesValue)`
    margin-right: 20px;
    margin-bottom: 5px;
    text-align: right;
  `,
  Metric: styled.div.withConfig({
    shouldForwardProp: (prop) => !['color'].includes(prop)
  })<{ color: string }>`
    color: ${({ color }) => color}
  `,
  AnscillaryContainer: styled.div`
    display: flex;
    margin-bottom: 5px;
  `,
  AnscillaryData: styled.div`
    text-transform: capitalize;
  `,
  AnscillaryDataLabel: styled.div`
    display: inline-block;
    cursor: pointer;
    width: 150px;
    padding-left: 31px;
  `,
  AnscillaryValueContainer: styled.div`
    display: inline-block;
    margin-left: 20px;
  `
}
